import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, Mail } from 'lucide-react';
import { useChatStore } from '@/store/chatStore';
import { useContactMessager } from '@/store/contactMessagerStore';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTeamConversation } from '@/hooks/useTeamConversation';
import { useMyConversations } from '@/hooks/useMyConversations';
import { useInquiries } from '@/hooks/useInquiries';
import { useNotifications } from '@/hooks/useNotifications';
import { readSigninCount } from '@/hooks/useSigninCounter';
import { cn } from '@/lib/utils';
import teamAvatar from '@/assets/cardsimp-team-avatar.png';

const PSEUDO_OPENED_KEY = 'cs.chat.pseudoOpened.v1';
const NEWCOMER_SIGNIN_THRESHOLD = 10;

const HIDE_PREFIXES = [
	'/auth',
	'/login',
	'/signup',
	'/onboarding',
	'/welcome',
	'/reset-password',
	'/logo-export',
];

/**
 * Always-on chat launcher. Picks the most relevant icon for the moment:
 *  - Unread DM / inquiry / team reply → Mail, lit gold + count badge
 *  - Unread notification only        → Bell, lit gold + count badge
 *  - New account (< 10 sign-ins)     → Pikachu team avatar (friendly prime)
 *  - Returning user, no unread       → Bell, soft chrome (not dark)
 *
 * Pre-register visitors keep the Pikachu + pseudo "1" welcome hook.
 */
export const ChatBubble = () => {
	const location = useLocation();
	const { user } = useAuth();
	const isMobile = useIsMobile();
	const { isOpen, isMinimized, open } = useChatStore();
	const contactOpen = useContactMessager((s) => s.isOpen);
	const contactMinimized = useContactMessager((s) => s.isMinimized);
	const { unread, messages } = useTeamConversation();
	const { unreadCount: dmUnread } = useMyConversations();
	const { data: inquiries = [] } = useInquiries();
	const { data: notifications = [] } = useNotifications();

	const inquiryUnread = useMemo(
		() => inquiries.filter((i) => !i.read_at).length,
		[inquiries]
	);
	const notifUnread = useMemo(
		() => notifications.filter((n) => !n.read).length,
		[notifications]
	);

	const [pseudoOpened, setPseudoOpened] = useState<boolean>(() => {
		if (typeof window === 'undefined') return false;
		try {
			return window.localStorage.getItem(PSEUDO_OPENED_KEY) === '1';
		} catch {
			return false;
		}
	});

	useEffect(() => {
		if ((isOpen || isMinimized) && !pseudoOpened) {
			try {
				window.localStorage.setItem(PSEUDO_OPENED_KEY, '1');
			} catch {}
			setPseudoOpened(true);
		}
	}, [isOpen, isMinimized, pseudoOpened]);

	const path = location.pathname;
	if (HIDE_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))) return null;
	if (user && isMobile) return null;
	if (isOpen || isMinimized || contactOpen || contactMinimized) return null;

	const isPreRegister = path === '/';

	const latest = messages[messages.length - 1];
	const teamUnread = !!(unread && latest?.kind === 'team_reply');
	const pseudoUnread = !user && !pseudoOpened;

	// Message-shaped unread (DMs, inquiries, team replies) vs notification-only unread.
	const messageUnreadCount = (teamUnread ? 1 : 0) + dmUnread + inquiryUnread;
	const hasMessageUnread = messageUnreadCount > 0;
	const hasNotifUnread = notifUnread > 0;
	const hasRealUnread = hasMessageUnread || hasNotifUnread;
	const showUnread = user ? hasRealUnread : pseudoUnread;

	// Newcomer = signed in but fewer than N completed sign-ins in this browser.
	const signinCount = readSigninCount(user?.id);
	const isNewcomer = !!user && signinCount > 0 && signinCount < NEWCOMER_SIGNIN_THRESHOLD;

	// Variant selection:
	//   Desktop → always Pikachu avatar (permanent identity in bottom-right).
	//   Mobile (pre-register only, signed-in mobile uses BottomNav speech-bubble):
	//     message unread → Mail, notif unread → Bell, else avatar.
	type Variant = 'mail' | 'bell' | 'avatar';
	let variant: Variant;
	if (!isMobile) {
		variant = 'avatar';
	} else if (hasMessageUnread) variant = 'mail';
	else if (hasNotifUnread) variant = 'bell';
	else if (!user || isNewcomer) variant = 'avatar';
	else variant = 'bell';

	const lit = showUnread;
	const totalUnread = messageUnreadCount + notifUnread;
	const badgeText = !user
		? '1'
		: totalUnread > 9
			? '9+'
			: String(totalUnread || 1);

	const handleOpen = () => {
		if (!user && !pseudoOpened) {
			try {
				window.localStorage.setItem(PSEUDO_OPENED_KEY, '1');
			} catch {}
			setPseudoOpened(true);
		}
		open();
	};

	return (
		<button
			type="button"
			onClick={handleOpen}
			aria-label="Open chat"
			className={cn(
				'group fixed z-[55] inline-flex items-center justify-center rounded-full transition-all duration-200 hover:scale-105 active:scale-95 animate-fade-in',
				'outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-active-gold/70 focus-visible:ring-offset-0',
				isPreRegister
					? 'top-[calc(env(safe-area-inset-top)+12px)] right-[calc(env(safe-area-inset-right)+16px)] md:top-auto md:bottom-[calc(env(safe-area-inset-bottom)+20px)] md:right-[calc(env(safe-area-inset-right)+20px)]'
					: 'bottom-[calc(env(safe-area-inset-bottom)+16px)] right-[calc(env(safe-area-inset-right)+16px)]'
			)}
		>
			{variant === 'avatar' ? (
				<span
					className={cn(
						'relative block h-[43px] w-[43px] rounded-full overflow-hidden transition-all duration-200',
						lit
							? 'opacity-100 ring-1 ring-active-gold/50 shadow-xl shadow-black/50'
							: 'opacity-95 ring-1 ring-active-gold/30 shadow-lg shadow-black/30 group-hover:opacity-100 group-hover:ring-active-gold/70'
					)}
				>
					<img
						src={teamAvatar}
						alt=""
						className="absolute inset-0 w-full h-full object-cover"
					/>
				</span>
			) : (
				<span
					className={cn(
						'relative inline-flex h-[43px] w-[43px] items-center justify-center rounded-full backdrop-blur-md transition-all duration-200',
						lit
							? 'bg-active-gold/10 ring-1 ring-active-gold/40 shadow-lg shadow-black/30'
							: 'bg-white/[0.06] ring-1 ring-active-gold/30 shadow-lg shadow-black/30 group-hover:bg-white/10 group-hover:ring-active-gold/70'
					)}
				>
					{variant === 'mail' ? (
						<Mail
							size={18}
							strokeWidth={1.75}
							className={cn(
								'transition-colors duration-200',
								lit ? 'text-active-gold' : 'text-white/70 group-hover:text-active-gold'
							)}
						/>
					) : (
						<Bell
							size={18}
							strokeWidth={1.75}
							className={cn(
								'transition-colors duration-200',
								lit ? 'text-active-gold' : 'text-white/70 group-hover:text-active-gold'
							)}
						/>
					)}
				</span>
			)}
			{showUnread && (
				<span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-destructive text-[10px] font-bold leading-none text-destructive-foreground ring-2 ring-background">
					{badgeText}
				</span>
			)}
		</button>
	);
};


export default ChatBubble;
